import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { useOrganization } from "@/auth/organization";
import { CardBadge, PaymentMethodModal, defaultMethod, usePaymentMethods } from "@/components/domains/shared";
import { Alert, Badge, Button, ErrorAlert, Field, Modal, Select, useToast, type Tone } from "@/components/ui";
import { attemptFinished, attemptKey } from "@/lib/attemptKey";
import { errorMessage } from "@/lib/http";
import {
  BULK_MAX,
  domainsApi,
  type BulkAction,
  type BulkChange,
  type BulkOutcome,
  type BulkRow,
  type BulkSelection,
} from "@/lib/domains";
import { formatMoney } from "@/lib/format";
import type { PaymentMethod } from "@/lib/types";

const ACTION_LABELS: Record<BulkAction, string> = {
  renew: "Renew",
  auto_renew_on: "Turn auto-renew on",
  auto_renew_off: "Turn auto-renew off",
  nameservers: "Change nameservers",
  contacts: "Change contacts",
};

const TERMS = Array.from({ length: 10 }, (_, i) => i + 1);

/** What one attempt to renew is for (the ticked domains and the term): the same request always gets the same key. */
function renewalScope(selection: BulkSelection, years: number): string {
  const text = JSON.stringify([selection, years]);
  let hash = 5381;
  for (let i = 0; i < text.length; i++) hash = ((hash << 5) + hash + text.charCodeAt(i)) | 0;
  return `${years}:${(hash >>> 0).toString(36)}`;
}

const OUTCOME: Record<BulkOutcome, { label: string; tone: Tone }> = {
  done: { label: "Done", tone: "success" },
  pending: { label: "Pending", tone: "info" },
  skipped: { label: "Skipped", tone: "neutral" },
  failed: { label: "Failed", tone: "danger" },
};

const ROLES = [
  ["admin", "Admin contact"],
  ["tech", "Tech contact"],
  ["billing", "Billing contact"],
] as const;

function download(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = name;
  anchor.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/** The results as a CSV a person can keep (built here: the server keeps nothing about a run). */
function resultsCsv(rows: BulkRow[]): Blob {
  const cell = (v: string) => {
    const safe = /^[=+\-@\t\r]/.test(v) ? `'${v}` : v;
    return /[",\n]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
  };
  const lines = [
    "Domain,Result,Reason",
    ...rows.map((r) => [r.domain, OUTCOME[r.outcome].label, r.reason ?? ""].map(cell).join(",")),
  ];
  return new Blob([lines.join("\n") + "\n"], { type: "text/csv;charset=utf-8" });
}

/** The bar above the list while domains are ticked: what to do with them. */
export function BulkBar({
  count,
  total,
  allMatching,
  selection,
  onSelectAllMatching,
  onClear,
}: {
  /** Ticked rows (or every matching domain once "Select all" is on). */
  count: number;
  /** Domains the filters match, across all pages. */
  total: number;
  allMatching: boolean;
  selection: BulkSelection;
  onSelectAllMatching: () => void;
  onClear: () => void;
}) {
  const { orgId, can } = useOrganization();
  const toast = useToast();
  const [action, setAction] = useState<BulkAction | null>(null);
  const tooMany = count > BULK_MAX;
  const exporting = useMutation({
    mutationFn: () => domainsApi.bulkExport(orgId, selection),
    onSuccess: (blob) => download(blob, "domains.csv"),
    onError: (e) =>
      toast.show({ tone: "warning", title: "The export didn't download", body: errorMessage(e) }),
  });
  const manage = can("domains:manage");
  const billing = can("billing:manage");
  const blocked = tooMany ? `Select up to ${BULK_MAX} domains` : undefined;

  return (
    <div
      className="flex flex-wrap items-center gap-2 rounded-[14px] border border-border bg-surface px-4 py-2.5"
      role="region"
      aria-label="Bulk actions"
    >
      <span className="text-[13px] font-semibold text-fg" aria-live="polite">
        {count} {count === 1 ? "domain" : "domains"} selected
      </span>
      {!allMatching && total > count && (
        <Button variant="secondary" size="sm" onClick={onSelectAllMatching}>
          Select all {total} matching
        </Button>
      )}
      {tooMany && (
        <span role="alert" className="text-[13px] font-semibold text-[#b52522]">
          Select up to {BULK_MAX} domains
        </span>
      )}
      <span className="flex-1" />
      <Button
        variant="brand"
        size="sm"
        disabledReason={
          blocked ??
          (manage
            ? billing
              ? undefined
              : "Only people who manage billing can renew domains"
            : "You need permission to manage domains")
        }
        onClick={() => setAction("renew")}
      >
        Renew
      </Button>
      <Button
        variant="secondary"
        size="sm"
        disabledReason={blocked ?? (manage ? undefined : "You need permission to manage domains")}
        onClick={() => setAction("auto_renew_on")}
      >
        Turn auto-renew on
      </Button>
      <Button
        variant="secondary"
        size="sm"
        disabledReason={blocked ?? (manage ? undefined : "You need permission to manage domains")}
        onClick={() => setAction("auto_renew_off")}
      >
        Turn auto-renew off
      </Button>
      <Button
        variant="secondary"
        size="sm"
        disabledReason={blocked ?? (manage ? undefined : "You need permission to manage domains")}
        onClick={() => setAction("nameservers")}
      >
        Change nameservers
      </Button>
      <Button
        variant="secondary"
        size="sm"
        disabledReason={blocked ?? (manage ? undefined : "You need permission to manage domains")}
        onClick={() => setAction("contacts")}
      >
        Change contacts
      </Button>
      <Button
        variant="secondary"
        size="sm"
        loading={exporting.isPending}
        disabledReason={blocked}
        onClick={() => exporting.mutate()}
      >
        Export CSV
      </Button>
      <Button variant="secondary" size="sm" onClick={onClear}>
        Clear
      </Button>
      {action && (
        <BulkDialog
          action={action}
          selection={selection}
          count={count}
          onClose={() => setAction(null)}
          onFinished={onClear}
        />
      )}
    </div>
  );
}

/** Choose the change, see who is skipped and why, then see a result for each domain. */
function BulkDialog({
  action,
  selection,
  count,
  onClose,
  onFinished,
}: {
  action: BulkAction;
  selection: BulkSelection;
  count: number;
  onClose: () => void;
  onFinished: () => void;
}) {
  const { orgId } = useOrganization();
  const qc = useQueryClient();
  const [mode, setMode] = useState<"cortexgrip" | "custom">("cortexgrip");
  const [servers, setServers] = useState("");
  const [roles, setRoles] = useState<Record<string, string>>({});
  const [years, setYears] = useState(1);
  const [card, setCard] = useState<PaymentMethod | null>(null);
  const [picking, setPicking] = useState(false);
  const renewing = action === "renew";
  const book = useQuery({
    queryKey: ["domains", orgId, "contact-book"],
    queryFn: () => domainsApi.contactBook(orgId),
    enabled: action === "contacts",
  });

  const nameservers = servers
    .split(/[\s,]+/)
    .map((s) => s.trim().toLowerCase().replace(/\.$/, ""))
    .filter(Boolean);
  const chosen = ROLES.filter(([role]) => roles[role]);
  const problem =
    action === "nameservers" && mode === "custom" && (nameservers.length < 2 || nameservers.length > 13)
      ? "Enter 2 to 13 nameservers"
      : action === "contacts" && chosen.length === 0
        ? "Choose at least one contact"
        : undefined;

  const change: BulkChange = {
    ...selection,
    action,
    ...(action === "nameservers"
      ? { nameserver_mode: mode, nameservers: mode === "custom" ? nameservers : [] }
      : {}),
    ...(action === "contacts"
      ? { contacts: Object.fromEntries(chosen.map(([role]) => [role, roles[role]])) }
      : {}),
    ...(renewing ? { years } : {}),
  };

  // One hidden key per attempt to renew these domains for this term: sending it twice pays once.
  const scope = renewalScope(selection, years);
  const preview = useMutation({ mutationFn: () => domainsApi.bulkPreview(orgId, change) });
  const run = useMutation({
    mutationFn: () =>
      renewing
        ? domainsApi.bulkRun(
            orgId,
            {
              ...change,
              payment_method_id: card?.id ?? null,
              expected_total_cents: preview.data?.total_cents ?? null,
            },
            attemptKey("bulk-renew", scope),
          )
        : domainsApi.bulkRun(orgId, change),
    onError: (err) => {
      if (renewing) attemptFinished("bulk-renew", scope, err);
    },
    onSuccess: () => {
      if (renewing) attemptFinished("bulk-renew", scope);
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: ["domains", orgId] }),
  });
  const total = preview.data?.total_cents ?? null;
  const money = total !== null ? formatMoney(total, preview.data?.currency ?? "GBP") : null;
  const error = preview.error ?? run.error;
  const step = run.data ? "results" : preview.data ? "summary" : "choose";
  const close = () => {
    if (run.data) onFinished();
    onClose();
  };

  return (
    <>
      <Modal
        open={!picking}
        onClose={close}
        size="lg"
        title={ACTION_LABELS[action]}
        subtitle={`${count} ${count === 1 ? "domain" : "domains"} selected`}
        footer={
          step === "choose" ? (
            <>
              <Button variant="secondary" size="lg" onClick={close}>
                Cancel
              </Button>
              <Button
                variant="brand"
                size="lg"
                loading={preview.isPending}
                disabledReason={problem}
                onClick={() => preview.mutate()}
              >
                Review
              </Button>
            </>
          ) : step === "summary" ? (
            <>
              <Button variant="secondary" size="lg" onClick={() => preview.reset()}>
                Back
              </Button>
              <Button
                variant="brand"
                size="lg"
                loading={run.isPending}
                disabledReason={preview.data?.will_change.length ? undefined : "No domain would change"}
                onClick={() => run.mutate()}
              >
                {renewing && money
                  ? `Pay ${money} & renew ${preview.data?.will_change.length ?? 0}`
                  : `Change ${preview.data?.will_change.length ?? 0} ${
                      preview.data?.will_change.length === 1 ? "domain" : "domains"
                    }`}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="secondary"
                size="lg"
                onClick={() => download(resultsCsv(run.data!.rows), "bulk-results.csv")}
              >
                Download results (CSV)
              </Button>
              <Button variant="brand" size="lg" onClick={close}>
                Close
              </Button>
            </>
          )
        }
      >
        <div className="flex flex-col gap-4">
          {error && <ErrorAlert error={error} />}

          {step === "choose" && action === "nameservers" && (
            <>
              <Field size="lg" label="Nameservers for all selected domains">
                {({ id }) => (
                  <Select
                    id={id}
                    value={mode}
                    onChange={(e) => setMode(e.target.value as "cortexgrip" | "custom")}
                  >
                    <option value="cortexgrip">CortexGrip nameservers</option>
                    <option value="custom">My own nameservers</option>
                  </Select>
                )}
              </Field>
              {mode === "custom" && (
                <Field
                  size="lg"
                  label="Custom nameservers"
                  hint="2 to 13, one per line or separated by commas"
                >
                  {({ id }) => (
                    <textarea
                      id={id}
                      rows={5}
                      value={servers}
                      onChange={(e) => setServers(e.target.value)}
                      placeholder={"ns1.example.com\nns2.example.com"}
                      className="rounded-[9px] border border-border bg-surface px-3 py-2 text-[13px] text-fg"
                    />
                  )}
                </Field>
              )}
            </>
          )}

          {step === "choose" && action === "contacts" && (
            <>
              <Alert tone="info">
                Pick the contacts to use. A role left empty stays as it is. The registrant can't be changed in
                bulk.
              </Alert>
              {book.isError && <ErrorAlert error={book.error} />}
              {ROLES.map(([role, label]) => (
                <Field key={role} size="lg" label={label}>
                  {({ id }) => (
                    <Select
                      id={id}
                      value={roles[role] ?? ""}
                      onChange={(e) => setRoles((r) => ({ ...r, [role]: e.target.value }))}
                    >
                      <option value="">Keep as it is</option>
                      {(book.data?.contacts ?? []).map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.label || c.full_name} ({c.email})
                        </option>
                      ))}
                    </Select>
                  )}
                </Field>
              ))}
            </>
          )}

          {step === "choose" && renewing && (
            <>
              <Field size="lg" label="Renew each domain for">
                {({ id }) => (
                  <Select id={id} value={years} onChange={(e) => setYears(Number(e.target.value))}>
                    {TERMS.map((n) => (
                      <option key={n} value={n}>
                        {n} {n === 1 ? "year" : "years"}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
              <p className="text-[13.5px] text-fg-muted">
                You will see the price of each domain and the total before anything is charged. A domain that
                doesn't allow this term is skipped.
              </p>
            </>
          )}

          {step === "choose" && (action === "auto_renew_on" || action === "auto_renew_off") && (
            <p className="text-[13.5px] text-fg-muted">
              {action === "auto_renew_on"
                ? "These domains will renew by themselves before they expire."
                : "These domains will not renew by themselves. They will expire unless you renew them."}
            </p>
          )}

          {step === "summary" && preview.data && (
            <>
              <p className="text-[14px] text-fg">
                <strong>{preview.data.will_change.length}</strong> of {preview.data.total}{" "}
                {preview.data.total === 1 ? "domain" : "domains"} will change:{" "}
                {ACTION_LABELS[action].toLowerCase()}.
              </p>
              {renewing && (
                <RenewalSummary
                  lines={preview.data.will_change}
                  total={money}
                  currency={preview.data.currency ?? "GBP"}
                  card={card}
                  onChange={() => setPicking(true)}
                />
              )}
              {preview.data.skipped.length > 0 && (
                <div className="flex flex-col gap-1.5">
                  <h3 className="text-[13px] font-semibold text-fg">
                    {preview.data.skipped.length} will be skipped
                  </h3>
                  <RowList rows={preview.data.skipped} />
                </div>
              )}
            </>
          )}

          {step === "results" && run.data && (
            <>
              <p className="text-[14px] text-fg" role="status">
                {run.data.done} done · {run.data.pending} pending · {run.data.skipped} skipped ·{" "}
                {run.data.failed} failed
              </p>
              <RowList rows={run.data.rows} />
            </>
          )}
        </div>
      </Modal>
      <PaymentMethodModal
        open={picking}
        oneTime
        selectedId={card?.id}
        onClose={() => setPicking(false)}
        onChosen={(m) => {
          setCard(m);
          setPicking(false);
        }}
      />
    </>
  );
}

/** What a bulk renewal costs, domain by domain, and the card it is charged to once. */
function RenewalSummary({
  lines,
  total,
  currency,
  card,
  onChange,
}: {
  lines: { id: string; domain: string; years?: number | null; price_cents?: number | null }[];
  total: string | null;
  currency: string;
  card: PaymentMethod | null;
  onChange: () => void;
}) {
  const methods = usePaymentMethods();
  const paying = card ?? defaultMethod(methods.data?.payment_methods);
  return (
    <>
      <ul className="max-h-[240px] divide-y divide-border overflow-y-auto rounded-[12px] border border-border">
        {lines.map((l) => (
          <li key={l.id} className="flex items-center gap-3 px-3 py-2 text-[13px]">
            <span className="min-w-0 flex-1 truncate font-semibold text-fg">{l.domain}</span>
            <span className="text-fg-muted">
              {l.years} {l.years === 1 ? "year" : "years"}
            </span>
            <span className="w-[90px] text-right font-semibold text-fg">
              {l.price_cents != null ? formatMoney(l.price_cents, currency) : "—"}
            </span>
          </li>
        ))}
      </ul>
      <div className="flex items-center gap-3 rounded-xl bg-[#faf6ef] p-3.5 text-sm">
        <span className="flex-1 font-semibold text-fg">Total, charged once (VAT included)</span>
        <span className="text-lg font-semibold text-fg">{total ?? "—"}</span>
      </div>
      <div className="flex items-center gap-3">
        {paying && <CardBadge brand={paying.brand} />}
        <p className="min-w-0 flex-1 text-sm font-semibold text-fg">
          {paying ? paying.label : "No card on file"}
        </p>
        <Button size="lg" variant="secondary" onClick={onChange}>
          {paying ? "Change" : "Add card"}
        </Button>
      </div>
    </>
  );
}

function RowList({ rows }: { rows: BulkRow[] }) {
  return (
    <ul className="max-h-[320px] divide-y divide-border overflow-y-auto rounded-[12px] border border-border">
      {rows.map((r) => (
        <li key={r.domain_id} className="flex items-start gap-3 px-3 py-2">
          <span className="min-w-0 flex-1 truncate text-[13px] font-semibold text-fg">{r.domain}</span>
          <Badge tone={OUTCOME[r.outcome].tone}>{OUTCOME[r.outcome].label}</Badge>
          {r.reason && <span className="w-[45%] text-[12.5px] text-fg-muted">{r.reason}</span>}
        </li>
      ))}
    </ul>
  );
}
